import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '@services/api';
import { useTranslationStore } from '@stores/translationStore';
import { useToast } from '@stores/toastStore';
import { useSignalR } from '@hooks/useSignalR';
import { playSound } from '../services/soundService';
import { resolveDisplayValue } from '../i18n';
import PageSpinner from '@components/PageSpinner';
import PageHeader from '@components/PageHeader';
import { emitActivityChanged } from '@utils/activityEvents';
import './HousekeepingPage.scss';

// Get hotel slug from URL params or localStorage fallback
const getHotelSlug = (paramSlug: string | undefined): string | null => {
  if (paramSlug && paramSlug !== 'undefined') return paramSlug;
  return localStorage.getItem('gcxp_hotel_slug');
};

interface HousekeepingItem {
  id: number;
  name: string;
  nameTranslationKey?: string;
  description?: string;
  icon: string;
  maxQuantity: number;
}

interface HousekeepingCategory {
  id: number;
  name: string;
  nameTranslationKey?: string;
  icon: string;
  items: HousekeepingItem[];
}

interface MenuResponse {
  isEnabled: boolean;
  welcomeMessage: string;
  estimatedDeliveryMinutes: number;
  maxRequestsPerDay: number;
  requestsToday: number;
  canRequestMore: boolean;
  guestName: string;
  roomNumber: string;
  categories: HousekeepingCategory[];
}

interface RequestItem {
  itemId: number;
  itemName: string;
  itemIcon: string;
  quantity: number;
}

interface HousekeepingRequest {
  id: string;
  status: string;
  statusChangedAt?: string;
  estimatedDeliveryTime?: string;
  deliveredAt?: string;
  createdAt: string;
  items: RequestItem[];
  progress: { step: number; label: string; isComplete: boolean; isCurrent: boolean }[];
}

interface CartItem {
  itemId: number;
  name: string;
  icon: string;
  quantity: number;
  maxQuantity: number;
}

type ViewState = 'selection' | 'cart' | 'placing' | 'tracking';

export default function HousekeepingPage() {
  const { hotelSlug: paramSlug } = useParams();
  const hotelSlug = getHotelSlug(paramSlug);
  const { loadTranslations, t, language } = useTranslationStore();
  const { toast } = useToast();

  const [view, setView] = useState<ViewState>('selection');
  const [menu, setMenu] = useState<MenuResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [notes, setNotes] = useState('');
  const [currentRequest, setCurrentRequest] = useState<HousekeepingRequest | null>(null);
  const { on } = useSignalR();
  const currentRequestIdRef = useRef<string | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Listen for real-time request status updates via SignalR
  useEffect(() => {
    const unsub = on('RequestStatusChanged', (data) => {
      const { requestId, status } = data as { requestId: string; status: string };
      if (requestId && currentRequestIdRef.current === requestId) {
        if (status === 'Delivered' || status === 'Completed') {
          playSound('request-ready');
        } else {
          playSound('notification');
        }
        apiClient.get<HousekeepingRequest>(`/api/v1/${hotelSlug}/housekeeping/request/${requestId}`)
          .then(res => setCurrentRequest(res.data))
          .catch(err => console.error('Failed to refresh request:', err));
      }
    });
    return unsub;
  }, [on, hotelSlug]);

  useEffect(() => {
    const controller = new AbortController();
    if (hotelSlug) {
      fetchMenu(controller.signal);
    }
    // Use translationStore.language (from localStorage) as source of truth
    if (hotelSlug && language) {
      loadTranslations(hotelSlug, 'housekeeping', language);
    }
    return () => controller.abort();
  }, [hotelSlug, language]);

  const fetchMenu = async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const response = await apiClient.get<MenuResponse>(`/api/v1/${hotelSlug}/housekeeping/menu`, { signal });
      setMenu(response.data);

      // Auto-expand first category
      if (response.data.categories.length > 0) {
        setExpandedCategory(response.data.categories[0].id);
      }

      // Don't auto-switch to tracking for existing requests
      // This allows multiple people in the same room to place separate requests
      // They can still see active requests via the "View Active Requests" section
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      console.error('Failed to fetch housekeeping menu:', err);
      setError('Unable to load housekeeping services');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (item: HousekeepingItem) => {
    setCart(prev => {
      const existing = prev.find(c => c.itemId === item.id);
      if (existing) {
        if (existing.quantity < item.maxQuantity) {
          return prev.map(c => 
            c.itemId === item.id 
              ? { ...c, quantity: c.quantity + 1 }
              : c
          );
        }
        return prev;
      }
      return [...prev, {
        itemId: item.id,
        name: item.name,
        icon: item.icon,
        quantity: 1,
        maxQuantity: item.maxQuantity
      }];
    });
  };

  const updateCartQuantity = (itemId: number, delta: number) => {
    setCart(prev => {
      return prev
        .map(item => {
          if (item.itemId === itemId) {
            const newQty = item.quantity + delta;
            if (newQty <= 0) return null;
            if (newQty > item.maxQuantity) return item;
            return { ...item, quantity: newQty };
          }
          return item;
        })
        .filter(Boolean) as CartItem[];
    });
  };

  const removeFromCart = (itemId: number) => {
    setCart(prev => prev.filter(item => item.itemId !== itemId));
  };

  const handleSubmitRequest = async () => {
    if (cart.length === 0) return;

    try {
      setSubmitting(true);
      setView('placing');

      const response = await apiClient.post<HousekeepingRequest>(
        `/api/v1/${hotelSlug}/housekeeping/request`,
        {
          items: cart.map(item => ({
            itemId: item.itemId,
            quantity: item.quantity
          })),
          notes: notes || undefined
        }
      );

      setCurrentRequest(response.data);
      setCart([]);
      setNotes('');
      setView('tracking');
      currentRequestIdRef.current = response.data.id;
      playSound('request-sent');
      emitActivityChanged();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: { message?: string }; Error?: string } } };
      const errorMessage = e.response?.data?.error?.message
        || e.response?.data?.Error
        || 'Failed to submit request. Please try again.';
      toast('error', errorMessage);
      setView('cart');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getTotalItems = () => cart.reduce((sum, item) => sum + item.quantity, 0);

  // Loading state
  if (loading) {
    return <PageSpinner />;
  }

  // Error state
  if (error && !menu) {
    return (
      <div className="housekeeping-page error">
        <div className="error-content">
          <span className="error-icon">⚠️</span>
          <h2>{t('common.oops')}</h2>
          <p>{error}</p>
          <button onClick={() => fetchMenu()} className="retry-btn">{t('common.tryAgain')}</button>
        </div>
      </div>
    );
  }

  // Not enabled
  if (menu && !menu.isEnabled) {
    return (
      <div className="housekeeping-page closed">
        <div className="closed-content">
          <span className="icon">🌙</span>
          <h2>{t('housekeeping.unavailable')}</h2>
          <p>{t('housekeeping.unavailableMsg')}</p>
          <p>{t('housekeeping.contactDesk')}</p>
        </div>
      </div>
    );
  }

  // Daily limit reached
  if (menu && !menu.canRequestMore) {
    return (
      <div className="housekeeping-page limit">
        <div className="limit-content">
          <span className="icon">✅</span>
          <h2>{t('housekeeping.limitReached')}</h2>
          <p>{t('housekeeping.tryTomorrow')}</p>
        </div>
      </div>
    );
  }

  // Placing request state
  if (view === 'placing') {
    return (
      <div className="housekeeping-page placing">
        <div className="placing-content">
          <div className="icon-spin">🛎️</div>
          <h2>{t('housekeeping.submitting')}</h2>
          <p>{t('housekeeping.sendingMsg')}</p>
        </div>
      </div>
    );
  }

  // Tracking view
  if (view === 'tracking' && currentRequest) {
    return (
      <div className="housekeeping-page tracking">
        <PageHeader title={<>🛎️ {t('housekeeping.yourRequest')}</>} />

        <div className="request-card">
          <div className="request-summary">
            <div className="items-list">
              {currentRequest.items.map((item, idx) => (
                <span key={idx} className="item-badge">
                  {item.itemIcon} {item.quantity}× {item.itemName}
                </span>
              ))}
            </div>
            <p className="request-time">{t('housekeeping.requested')} {formatTime(currentRequest.createdAt)}</p>
          </div>

          <div className="progress-tracker">
            {currentRequest.progress.map((step, index) => (
              <div 
                key={step.step}
                className={`progress-step ${step.isComplete ? 'complete' : ''} ${step.isCurrent ? 'current' : ''}`}
              >
                <div className="step-indicator">
                  {step.isComplete ? '✓' : step.step}
                </div>
                <div className="step-content">
                  <span className="step-label">{step.label}</span>
                </div>
                {index < currentRequest.progress.length - 1 && (
                  <div className={`step-connector ${step.isComplete ? 'complete' : ''}`} />
                )}
              </div>
            ))}
          </div>

          {currentRequest.status === 'Ready' && (
            <div className="ready-alert">
              <span>🚶</span>
              <p>{t('housekeeping.onTheWay')}</p>
            </div>
          )}

          {currentRequest.status === 'Delivered' && (
            <div className="delivered-alert">
              <span>🎉</span>
              <p>{t('housekeeping.delivered')}</p>
            </div>
          )}

          {currentRequest.estimatedDeliveryTime && currentRequest.status !== 'Delivered' && (
            <div className="eta-info">
              <span>⏱️</span>
              <p>{t('housekeeping.estimatedDelivery')}: {formatTime(currentRequest.estimatedDeliveryTime)}</p>
            </div>
          )}
        </div>

        <button 
          className="new-request-btn"
          onClick={() => {
            setCurrentRequest(null);
            setView('selection');
            fetchMenu();
          }}
        >
          {t('housekeeping.makeAnother')}
        </button>
      </div>
    );
  }

  // Cart view
  if (view === 'cart') {
    return (
      <div className="housekeeping-page cart-view">
        <PageHeader
          title={<>🛒 {t('housekeeping.yourRequest')}</>}
          leading={
            <button className="back-btn" onClick={() => setView('selection')}>
              ← {t('common.back')}
            </button>
          }
        />

        {error && (
          <div className="inline-error">
            <span>⚠️</span> {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="cart-items">
          {cart.map(item => (
            <div key={item.itemId} className="cart-item">
              <span className="item-icon">{item.icon}</span>
              <div className="item-info">
                <span className="item-name">{item.name}</span>
              </div>
              <div className="quantity-controls">
                <button onClick={() => updateCartQuantity(item.itemId, -1)}>−</button>
                <span>{item.quantity}</span>
                <button 
                  onClick={() => updateCartQuantity(item.itemId, 1)}
                  disabled={item.quantity >= item.maxQuantity}
                >+</button>
              </div>
              <button className="remove-btn" onClick={() => removeFromCart(item.itemId)}>
                🗑️
              </button>
            </div>
          ))}
        </div>

        <div className="notes-section">
          <label>{t('housekeeping.specialRequests')}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('housekeeping.specialPlaceholder')}
            maxLength={500}
            aria-label={t('housekeeping.specialRequests')}
          />
          <div className="text-muted small text-end">{notes.length}/500</div>
        </div>

        <div className="cart-footer">
          <div className="delivery-info">
            <span>⏱️</span>
            <p>{t('housekeeping.estimatedDelivery')}: ~{menu?.estimatedDeliveryMinutes || 15} {t('housekeeping.minutes')}</p>
          </div>
          <button
            className="submit-btn"
            onClick={handleSubmitRequest}
            disabled={cart.length === 0 || submitting}
          >
            {t('housekeeping.submitRequest')} ({getTotalItems()} {getTotalItems() !== 1 ? t('housekeeping.items') : t('housekeeping.item')})
          </button>
        </div>
      </div>
    );
  }

  // Selection view (default)
  return (
    <div className="housekeeping-page selection page-content">
      <PageHeader
        title={<>🛎️ {t('housekeeping.title')}</>}
        subtitle={
          menu?.welcomeMessage ||
          (menu?.roomNumber ? `${t('housekeeping.room')} ${menu.roomNumber}` : undefined)
        }
      />

      {error && (
        <div className="inline-error">
          <span>⚠️</span> {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="categories">
        {menu?.categories.map(category => (
          <div key={category.id} className="category">
            <button 
              className={`category-header ${expandedCategory === category.id ? 'expanded' : ''}`}
              onClick={() => setExpandedCategory(
                expandedCategory === category.id ? null : category.id
              )}
            >
              <span className="category-icon">{category.icon}</span>
              <span className="category-name">{resolveDisplayValue(category.nameTranslationKey || category.name, t)}</span>
              <span className="expand-icon">{expandedCategory === category.id ? '▼' : '▶'}</span>
            </button>
            
            {expandedCategory === category.id && (
              <div className="category-items">
                {category.items.map(item => {
                  const inCart = cart.find(c => c.itemId === item.id);
                  return (
                    <div key={item.id} className="item-card">
                      <span className="item-icon">{item.icon}</span>
                      <div className="item-info">
                        <span className="item-name">{resolveDisplayValue(item.nameTranslationKey || item.name, t)}</span>
                        {item.description && (
                          <span className="item-desc">{resolveDisplayValue(item.description, t)}</span>
                        )}
                      </div>
                      {inCart ? (
                        <div className="quantity-controls">
                          <button onClick={() => updateCartQuantity(item.id, -1)}>−</button>
                          <span>{inCart.quantity}</span>
                          <button 
                            onClick={() => updateCartQuantity(item.id, 1)}
                            disabled={inCart.quantity >= item.maxQuantity}
                          >+</button>
                        </div>
                      ) : (
                        <button 
                          className="add-btn"
                          onClick={() => addToCart(item)}
                        >
                          + {t('housekeeping.add')}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {cart.length > 0 && (
        <div className="cart-fab">
          <button onClick={() => setView('cart')}>
            🛒 {t('housekeeping.viewCart')} ({getTotalItems()})
          </button>
        </div>
      )}
    </div>
  );
}
